/**
 * Per-process confidence_policy bands narrower than the generic 0 <= freigabe <= auto <= 1.
 *
 * Shared by the api (admin PUT /admin/confidence-policy/:process and the platform-admin tenant
 * PATCH) and the web (UnterschriftenPage pre-check + sliders, WorkflowsPage row labels), so the
 * band and its German message exist exactly once.
 *
 * [Source: docs/product/p4-p6-slices/p4-FU2-signature-thresholds-server-bounds-design.md] (§1.1, §2, §4)
 * [Source: docs/product/2026-10-15_Brokly_Prozesskonzept_P4_Analysemandatsabschluss_V2.0.txt] (Kap. 7.10)
 */

import type { TaskProcess } from './task';

/** Stefan P4 V2.0 Kap. 7.10, Ähnlichkeitssuche von Unterschriften: the concept Bandbreite. */
export const SIGNATURE_THRESHOLD_BOUNDS = {
  /** Schwellwert automatische Annahme. */
  auto: { min: 0.7, max: 0.99 },
  /** Untergrenze unsicher; its max is the current auto value. */
  lower: { min: 0.5 },
  /** Slider step (UI only); storage is numeric(4,3), the server does not force this grid. */
  step: 0.01,
} as const;

export type ConfidencePolicyBand = {
  readonly auto: { readonly min: number; readonly max: number };
  readonly lower: { readonly min: number };
  readonly step: number;
};

/** Per-process bands narrower than the generic 0..1. Only mandate_signing today. */
export const CONFIDENCE_POLICY_BOUNDS: Partial<Record<TaskProcess, ConfidencePolicyBand>> = {
  mandate_signing: SIGNATURE_THRESHOLD_BOUNDS,
};

export const CONFIDENCE_POLICY_OUT_OF_BOUNDS = 'confidence_policy_out_of_bounds' as const;

/** 3-decimal normalisation = what numeric(4,3) holds. The api validates and stores this value. */
export function normalizeThreshold(n: number): number {
  return Math.round(n * 1000) / 1000;
}

export interface ConfidencePolicyBoundsViolation {
  code: typeof CONFIDENCE_POLICY_OUT_OF_BOUNDS;
  field: 'auto_threshold' | 'freigabe_threshold';
  /** German, built from the constants; identical in the api 400 and the web pre-check. */
  message: string;
}

/** Integer thousandths, so 0.7, Number('0.700') and 0.7000000001 compare exactly on the edges. */
function milli(n: number): number {
  return Math.round(n * 1000);
}

/** Own keys only: a process string like 'constructor' must not resolve to an Object.prototype member. */
function bandFor(process: string): ConfidencePolicyBand | null {
  return Object.prototype.hasOwnProperty.call(CONFIDENCE_POLICY_BOUNDS, process)
    ? (CONFIDENCE_POLICY_BOUNDS as Record<string, ConfidencePolicyBand>)[process]!
    : null;
}

function fmt(n: number): string {
  return n.toFixed(2);
}

/**
 * Pure. null = within the band, or the process has no band. Edges are inclusive.
 * Meant to run after the generic 0 <= freigabe <= auto <= 1 check, on normalised values; a
 * non-finite input is reported as a violation (never a throw).
 */
export function checkConfidencePolicyBounds(
  process: string,
  auto: number,
  freigabe: number,
): ConfidencePolicyBoundsViolation | null {
  const band = bandFor(process);
  if (band == null) return null;

  const a = milli(auto);
  if (!Number.isFinite(a) || a < milli(band.auto.min) || a > milli(band.auto.max)) {
    return {
      code: CONFIDENCE_POLICY_OUT_OF_BOUNDS,
      field: 'auto_threshold',
      message: `Schwellwert automatische Annahme muss zwischen ${fmt(band.auto.min)} und ${fmt(band.auto.max)} liegen (Prozess ${process}).`,
    };
  }

  const l = milli(freigabe);
  if (!Number.isFinite(l) || l < milli(band.lower.min) || l > a) {
    return {
      code: CONFIDENCE_POLICY_OUT_OF_BOUNDS,
      field: 'freigabe_threshold',
      message: `Untergrenze unsicher muss mindestens ${fmt(band.lower.min)} und höchstens der Schwellwert sein (Prozess ${process}).`,
    };
  }

  return null;
}

/**
 * Pure. Clamps a pair into the process's band (auto into [auto.min, auto.max], freigabe into
 * [lower.min, auto]); a process without a band gets the pair unchanged. Used by the wizard
 * presets, which set one pair across every process.
 */
export function clampToConfidencePolicyBand(
  process: string,
  auto: number,
  freigabe: number,
): { auto: number; freigabe: number } {
  const band = bandFor(process);
  if (band == null) return { auto, freigabe };
  const a = Math.min(Math.max(auto, band.auto.min), band.auto.max);
  const f = Math.min(Math.max(freigabe, band.lower.min), a);
  return { auto: a, freigabe: f };
}

/** Label suffix for a band ("0.70 bis 0.99"), or null when the process has none (WorkflowsPage). */
export function confidencePolicyBandLabel(
  process: string,
): { auto: string; freigabe: string } | null {
  const band = bandFor(process);
  if (band == null) return null;
  return {
    auto: `${fmt(band.auto.min)} bis ${fmt(band.auto.max)}`,
    freigabe: `${fmt(band.lower.min)} bis Auto-Schwelle`,
  };
}
